"use client";

import { FolderSimpleIcon } from "@phosphor-icons/react/dist/ssr";
import Link from "next/link";
import { useTranslations } from "next-intl";

import AssetWrapper from "@/components/ui/assetWrapper";
import EmptyStateMessage from "@/components/ui/emptyStateMessage";
import { cn } from "@/lib/utils";
import type { RecentProjectItem } from "@/types";

type RecentProjectProps = {
  items: RecentProjectItem[] | undefined;
};

export function RecentProjectsTable({ items }: RecentProjectProps) {
  const t = useTranslations("home");
  const isEmpty = !items || items.length === 0;

  return (
    <div
      className={cn(
        "flex w-full flex-1 flex-col",
        isEmpty ? "h-35.75 items-center justify-center" : "items-start gap-2"
      )}
    >
      {isEmpty ? (
        <div className="flex items-center justify-center">
          <EmptyStateMessage
            message={t("empty.recentProjects")}
            icon={
              <span className="color-neutral-950">
                <FolderSimpleIcon />
              </span>
            }
            compact
          />
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center gap-2">
            <div className="flex size-6 shrink-0 flex-row items-center justify-center gap-[5.54px] rounded-lg bg-[#D5F2F0] p-0">
              <span className="text-[#1A1A1A] [&_svg]:size-[14px]">
                <FolderSimpleIcon />
              </span>
            </div>
            <span className="heading-6 translate-y-0.5 text-neutral-950">
              {t("section.recentProjects")}
            </span>
          </div>

          {/* Card */}
          <div className="w-full">
            <table className="w-full border-collapse">
              <tbody className="flex flex-col gap-1 [&>tr:nth-child(n+4)]:hidden 2xl:[&>tr:nth-child(n+4)]:grid">
                {items?.map((item, index) => (
                  <tr
                    key={item.project_code + index}
                    className="relative grid grid-cols-[minmax(0,auto)_minmax(0,auto)_minmax(0,auto)_minmax(0,1fr)] items-center gap-2 rounded-md border-b border-neutral-100 bg-neutral-50 py-3 pr-2 pl-2 transition-colors hover:bg-neutral-100 xl:gap-3 2xl:py-4"
                  >
                    {/* Coluna 1: Project Code */}
                    <td className="flex min-w-0 items-center">
                      <span
                        className="block translate-y-px truncate font-medium tracking-[-0.48px] text-neutral-950"
                        style={{
                          fontSize: "clamp(0.625rem, 0.8vw, 0.75rem)",
                          lineHeight: "normal",
                        }}
                      >
                        {item.project_code}
                      </span>
                      <Link
                        href={`/projects/${item.project_code}`}
                        aria-label={t("projectCard.open")}
                        className="focus-visible:ring-ring absolute inset-0 rounded-md focus-visible:ring-2 focus-visible:outline-none"
                      />
                    </td>

                    {/* Coluna 2: Standard Acronym */}
                    <td className="flex min-w-0 items-center">
                      <span
                        className="block translate-y-px truncate font-medium tracking-[-0.48px] text-neutral-950"
                        style={{
                          fontSize: "clamp(0.625rem, 0.8vw, 0.75rem)",
                          lineHeight: "normal",
                        }}
                      >
                        {item.standard_acronym}
                      </span>
                    </td>

                    {/* Coluna 3: Site Name */}
                    <td className="flex min-w-0 items-center">
                      <span
                        className="block translate-y-px truncate font-medium tracking-[-0.48px] text-neutral-950"
                        style={{
                          fontSize: "clamp(0.625rem, 0.8vw, 0.75rem)",
                          lineHeight: "normal",
                        }}
                      >
                        {item.site_name}
                      </span>
                    </td>

                    {/* Coluna 4: Assets */}

                    <td className="flex min-w-0 items-center [&_button]:relative [&_button]:z-10">
                      <AssetWrapper assets={item.assets} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
